import React, { useCallback } from 'react';
import {
  bool, func, string, number,
} from 'prop-types';
import styled, { css } from 'styled-components';
import {
  Flex, Typography, ImageBackground, Circle,
} from '@atoms';
import { formatPrice } from '@helpers/menuItemDetails';

const MenuItem = ({
  imageSrc,
  isMustTry,
  name,
  isActive,
  price,
  subtitle,
  onMenuItemSelect,
  subcategoryId,
  specialPrice,
}) => {
  const getItemPriceComponent = useCallback(() => {
    if (!specialPrice && specialPrice !== 0) {
      return (
        <Circle
          diameter={33}
          grow={0}
          justifyCenter
          itemsCenter
          backgroundColor="darkGrey"
        >
          <Typography size={(price / 100) % 1 ? 'xs' : 'lg'} color="white" weight="bold">
            {`$${formatPrice(price)}`}
          </Typography>
        </Circle>
      );
    }

    return (
      <StyledPriceWrapper row justifyEnd>
        <Circle
          diameter={33}
          grow={0}
          justifyCenter
          itemsCenter
          backgroundColor="strawberry"
        >
          <Typography size={(price / 100) % 1 ? 'xs' : 'lg'} color="white" weight="medium">
            {`$${formatPrice(price)}`}
          </Typography>
        </Circle>
        <Circle
          diameter={33}
          grow={0}
          justifyCenter
          itemsCenter
          backgroundColor="white"
        >
          <Typography size={(specialPrice / 100) % 1 ? 'xs' : 'lg'} color="grannySmith" weight="bold">
            {`$${formatPrice(specialPrice)}`}
          </Typography>
        </Circle>
      </StyledPriceWrapper>
    );
  }, [price, specialPrice]);

  return (
    <Wrapper
      column
      padding="md"
      imageSrc={imageSrc}
      onClick={onMenuItemSelect}
      isActive={isActive}
      id={subcategoryId}
    >
      <StyledItemHeader isActive={isActive}>
        {!isActive && (
          <InactiveItemMessageWrapper justifyCenter itemsCenter grow={0}>
            <Typography color="white" size="sm">
              Item is inactive
            </Typography>
          </InactiveItemMessageWrapper>
        )}
        {getItemPriceComponent()}
      </StyledItemHeader>
      <Flex column justifyEnd>
        {isMustTry && (
          <Typography size="xs" weight="bold" color="white">
            [*A MUST TRY*]
          </Typography>
        )}
        <StyledTypography size="lg" weight="black" color="white">
          {name}
        </StyledTypography>
        {subtitle && (
          <SubtitleWrapper size="md" weight="bold" color="white">
            {subtitle}
          </SubtitleWrapper>
        )}
      </Flex>
    </Wrapper>
  );
};

const StyledItemHeader = styled(Flex)`
  ${({ isActive }) => css`
    justify-content: ${isActive ? 'flex-end' : 'space-between'};
  `}
`;

const InactiveItemMessageWrapper = styled(Flex)`
  ${({ theme: { color } }) => css`
    width: 100px;
    height: 20px;
    border-radius: 10px;
    background-color: ${color.strawberry};
  `}
`;

const Wrapper = styled(ImageBackground)`
  ${({ theme: { color }, isActive }) => css`
    width: 50%;
    max-width: 50%;
    height: 55vw;
    border: ${isActive ? `1px solid ${color.warmGrey}` : `2px solid ${color.strawberry}`};
  `}
`;

const SubtitleWrapper = styled(Typography)`
  ${() => css`
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const StyledPriceWrapper = styled(Flex)`
  ${({ theme: { spacing } }) => css`
    div:first-child {
      margin-right: ${spacing.md};

      p {
        text-decoration: line-through;
      }
    }
  `}
`;

const StyledTypography = styled(Typography)`
  ${({ theme: { color } }) => css`
    text-shadow: 1px 1px ${color.black};
  `}
`;

MenuItem.defaultProps = {
  isMustTry: false,
  imageSrc: '',
  specialPrice: null,
};

MenuItem.propTypes = {
  specialPrice: number,
  imageSrc: string,
  isMustTry: bool,
  onMenuItemSelect: func.isRequired,
  name: string.isRequired,
  isActive: bool.isRequired,
  price: number.isRequired,
  subtitle: string.isRequired,
  subcategoryId: string.isRequired,
};

export default MenuItem;
